import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useId, useState } from 'react';
import { Button } from '../../../components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { TextingGraphic } from '../../svgs/graphics/TextingGraphic';
import { styleSheet } from './styles';

interface IProps {
	ctaLabel?: string;
	exclusions?: Api.IPhoneNumber[];
	onPhoneNumberSelected: (phoneNumber: Api.IPhoneNumber) => void;
	recipient: Api.TextRecipientViewModel;
}

const formatPhoneLabel = (phone: Api.IPhoneNumber): string => {
	const number = phone.metadata?.standard || phone.value;
	return phone.label ? `${phone.label}: ${number}` : number;
};

const getRecipientPhoneOptions = (recipient: Api.TextRecipientViewModel, exclusions: Api.IPhoneNumber[] = []) => {
	return Api.VmUtils.getTextMessageCapablePhoneNumbers(recipient?.phoneNumbers).filter(
		p => !exclusions.some(e => p.metadata?.standard === e.metadata?.standard)
	);
};

const getDefaultPhone = (recipient: Api.TextRecipientViewModel) => {
	if (!recipient.phoneNumbers?.length) {
		return null;
	}

	return Api.VmUtils.getTextMessageCapablePhoneNumbers(recipient.phoneNumbers)[0] || recipient.phoneNumbers[0];
};

export const MultiPhoneSelect: React.FC<IProps> = ({
	ctaLabel = 'Use this number',
	exclusions = [],
	onPhoneNumberSelected,
	recipient,
}) => {
	const id = useId();
	const options = getRecipientPhoneOptions(recipient, exclusions);
	const defaultPhone = getDefaultPhone(recipient);
	const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<Api.IPhoneNumber | undefined>(
		options.find(o => o.metadata?.e164 === defaultPhone?.metadata?.e164)
	);
	const onUseThisNumberClick = () => {
		if (selectedPhoneNumber) {
			onPhoneNumberSelected(selectedPhoneNumber);
		}
	};

	if (!options?.length) {
		// no viable options to choose from
		// Possible: "exclusions" list contains all of "recipient.phoneNumbers", or "recipient.phoneNumbers"
		return (
			<div className={css(styleSheet.selectContainer, styleSheet.error)}>
				There are no valid phone number options for this recipient.
			</div>
		);
	}

	return (
		<div className={css(styleSheet.selectContainer)}>
			<Select
				value={selectedPhoneNumber}
				onValueChange={setSelectedPhoneNumber}
				itemToStringLabel={option => formatPhoneLabel(option)}
				isItemEqualToValue={(option, value) => option.metadata?.e164 === value.metadata?.e164}
			>
				<SelectTrigger styles={[styleSheet.multiplePhoneNumbersSelect]}>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					{options.map(option => (
						<SelectItem key={`${id}-${option.value}`} value={option}>
							{formatPhoneLabel(option)}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{!selectedPhoneNumber?.metadata?.standard && (
				<p className={css(styleSheet.error)}>This number is invalid. Please edit it before continuing.</p>
			)}
			<Button
				styles={[styleSheet.useThisNumberCta]}
				onClick={onUseThisNumberClick}
				disabled={!selectedPhoneNumber?.metadata?.standard}
			>
				{ctaLabel}
			</Button>
		</div>
	);
};

export const NoConversationMultiPhoneSelect: React.FC<IProps> = props => {
	return (
		<div className={css(styleSheet.multiplePhoneNumbersSelectContainer)}>
			<div className={css(styleSheet.textingGraphicContainer)}>
				<TextingGraphic animate={true} />
			</div>
			<div className={css(styleSheet.header)}>No text conversation started</div>
			<div className={css(styleSheet.gray)}>This contact has multiple phone numbers.</div>
			<div className={css(styleSheet.gray)}>Text with:</div>
			<MultiPhoneSelect {...props} />
		</div>
	);
};
